import type { CRMUser, EmployeeDesignation, ExecutiveBriefingProfile, User } from '../types';
import { canManageBillingPayables } from './billingPayablesAccess';
import { hasOfficeOpsPrivileges } from './employeeDesignations';

/** True when either the auth session or hydrated CRM user row marks 1099 contractor. */
export function resolveIs1099Contractor(args: {
  authFlag?: boolean;
  crmFlag?: boolean;
}): boolean {
  return args.authFlag === true || args.crmFlag === true;
}

/** Prefer auth profile designations; fall back to hydrated CRM users row. */
export function resolveEmployeeDesignations(
  user: Pick<User, 'id' | 'designations'> | null | undefined,
  crmUsers: CRMUser[],
): EmployeeDesignation[] | undefined {
  const me = user?.id ? crmUsers.find(u => u.uid === user.id) : undefined;
  return user?.designations ?? me?.designations;
}

/** Admin, EA, or Office Manager — contractor payables inbox. */
export function canManageContractorPayables(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  if (args.authRole === 'admin') return true;
  return hasOfficeOpsPrivileges(args.designations);
}

/** May log contractor hours on behalf of another user. */
export function canEnterContractorTimeOnBehalf(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  return canManageContractorPayables(args);
}

/** May open the contractor timesheets page for self or on-behalf entry. */
export function canAccessContractorTimesheets(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
  is1099Contractor?: boolean;
}): boolean {
  if (args.authRole === 'admin') return true;
  // Engineers are external portal users — hours are not logged via contractor timesheets.
  if (args.authRole === 'engineer') return false;
  if (args.is1099Contractor) return true;
  return canEnterContractorTimeOnBehalf(args);
}

/** Finances hub contractor payables section — admin, EA, or CPA (read). */
export function canAccessContractorPayablesFinances(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  if (args.authRole === 'cpa') return true;
  return canManageContractorPayables(args);
}

export interface FinancesHubAccessArgs {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
  briefingProfile?: ExecutiveBriefingProfile | null;
}

/** Full Finances hub (forecast + capital plans) — admin, EA, CFO, COO, or CPA. */
export function canAccessFullFinancesHub(args: FinancesHubAccessArgs): boolean {
  if (args.authRole === 'cpa') return true;
  return canManageBillingPayables(args);
}

/** CapEx thread replies — forecast readers, including the CPA portal. */
export function canCommentOnFinances(args: FinancesHubAccessArgs): boolean {
  return canAccessFullFinancesHub(args);
}

/** May edit overhead, payroll, and finance settings — admin or EA only. */
export function canWriteFinancesModule(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  return canManageContractorPayables(args);
}

/** 1099-NEC reporting threshold (USD). */
export const CONTRACTOR_1099_NEC_THRESHOLD = 600;
